import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Link } from "react-router-dom";
import { Bell, Check, AlertCircle, AlertTriangle, Info, X, CheckCheck } from "lucide-react";

const KIND_STYLE = {
  info: { Icon: Info, cls: "text-primary bg-primary/10" },
  warning: { Icon: AlertTriangle, cls: "text-amber-700 bg-amber-100" },
  danger: { Icon: AlertCircle, cls: "text-red-700 bg-red-100" },
  success: { Icon: Check, cls: "text-emerald-700 bg-emerald-100" },
};

export default function NotificationsBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const countRef = useRef(null);

  const loadCount = async () => {
    try {
      const { data } = await api.get("/notifications/count");
      const n = data.unread || 0;
      // Browser Push: if count increased and permission granted, show native notification
      if (n > countRef.current && countRef.current !== null && "Notification" in window && Notification.permission === "granted") {
        const delta = n - countRef.current;
        try {
          new Notification("محاسبتي — لديك إشعارات جديدة", {
            body: `${delta} إشعار${delta > 1 ? "ات" : ""} جديد${delta > 1 ? "ة" : ""} — اضغط لعرضها`,
            icon: "/logo192.png", tag: "muhasabaty-notif", renotify: true,
          });
        } catch {}
      }
      countRef.current = n;
      setCount(n);
    } catch {}
  };
  const loadList = async () => {
    setLoading(true);
    try { const { data } = await api.get("/notifications"); setItems(data); }
    catch {}
    finally { setLoading(false); }
  };

  const scanAlerts = async () => {
    // Generate operational alerts (low stock, expiry, overdue invoices, cheques) on demand.
    try { await api.post("/notifications/scan"); } catch {}
  };

  useEffect(() => {
    (async () => { await scanAlerts(); await loadCount(); })();
    const t = setInterval(loadCount, 30000);
    const s = setInterval(scanAlerts, 15 * 60 * 1000); // rescan every 15 min
    // Ask for push permission once (non-blocking, user can grant later via bell menu)
    if ("Notification" in window && Notification.permission === "default" && !localStorage.getItem("push_asked")) {
      setTimeout(() => {
        localStorage.setItem("push_asked", "1");
        Notification.requestPermission().catch(() => {});
      }, 6000);
    }
    return () => { clearInterval(t); clearInterval(s); };
  }, []);
  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const toggle = () => { const n = !open; setOpen(n); if (n) loadList(); };

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setItems(prev => prev.map(x => x.id === id ? { ...x, read: true } : x));
      loadCount();
    } catch {}
  };
  const markAll = async () => {
    try { await api.put("/notifications/read-all"); setItems(prev => prev.map(x => ({...x, read: true}))); loadCount(); }
    catch {}
  };
  const del = async (id) => {
    try { await api.delete(`/notifications/${id}`); setItems(prev => prev.filter(x => x.id !== id)); loadCount(); }
    catch {}
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} className="relative w-9 h-9 grid place-items-center rounded-md border border-border hover:bg-muted transition" data-testid="notif-bell">
        <Bell className="w-4 h-4" />
        {count > 0 && (
          <span className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center" data-testid="notif-count">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute end-0 mt-2 w-[360px] max-h-[70vh] bg-background border border-border rounded-md shadow-xl z-50 flex flex-col" data-testid="notif-panel">
          <div className="px-3 py-2 border-b border-border flex items-center justify-between">
            <div className="font-heading font-bold text-sm">الإشعارات</div>
            {items.some(x => !x.read) && (
              <button onClick={markAll} className="text-[11px] text-primary flex items-center gap-1 hover:underline" data-testid="mark-all-read"><CheckCheck className="w-3 h-3" /> قراءة الكل</button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && <div className="p-4 text-xs text-muted-foreground text-center">جاري التحميل...</div>}
            {!loading && items.length === 0 && <div className="p-6 text-xs text-muted-foreground text-center">لا توجد إشعارات</div>}
            {items.map(n => {
              const style = KIND_STYLE[n.kind] || KIND_STYLE.info;
              const Icon = style.Icon;
              const inner = (
                <div className={`flex items-start gap-2 p-3 border-b border-border last:border-0 hover:bg-muted/50 transition ${!n.read ? "bg-primary/5" : ""}`}>
                  <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${style.cls}`}><Icon className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold truncate">{n.title}</div>
                    <div className="text-[11px] text-muted-foreground line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString("ar-EG", { dateStyle: "short", timeStyle: "short" })}</div>
                  </div>
                  <div className="flex flex-col gap-1">
                    {!n.read && <button onClick={(e)=>{e.preventDefault(); e.stopPropagation(); markRead(n.id);}} className="text-primary text-[10px]" title="قراءة" data-testid={`mark-read-${n.id}`}><Check className="w-3 h-3" /></button>}
                    <button onClick={(e)=>{e.preventDefault(); e.stopPropagation(); del(n.id);}} className="text-destructive text-[10px]" title="حذف" data-testid={`del-notif-${n.id}`}><X className="w-3 h-3" /></button>
                  </div>
                </div>
              );
              return n.link ? (
                <Link key={n.id} to={n.link} onClick={()=>{setOpen(false); if(!n.read) markRead(n.id);}} className="block" data-testid={`notif-${n.id}`}>{inner}</Link>
              ) : (
                <div key={n.id} onClick={()=>{ if(!n.read) markRead(n.id);}} className="cursor-pointer" data-testid={`notif-${n.id}`}>{inner}</div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
